import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useBasePath } from '@/lib/paths'
import { InboundEmailDetailed, BusinessRef } from '@/types'
import { DocumentPreviewModal } from '@/components/shared/DocumentPreviewModal'
import { Mail, Paperclip, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'

type Props = {
  business: BusinessRef
  inbound_email: InboundEmailDetailed
}

export default function BusinessInboundEmailsShow({ business, inbound_email }: Props) {
  const basePath = useBasePath()

  return (
    <DashboardLayout
      title={inbound_email.subject || '(no subject)'}
      breadcrumbs={[
        { label: 'Businesses', href: 'businesses' },
        { label: business.name, href: `businesses/${business.id}` },
        { label: 'Emails', href: `businesses/${business.id}/inbound_emails` },
        { label: inbound_email.subject || '(no subject)' },
      ]}
    >
      <div className="space-y-6">
        {/* Header */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Mail className="w-4 h-4 text-muted-foreground" />
            <h1 className="text-xl font-semibold tracking-tight">
              {inbound_email.subject || '(no subject)'}
            </h1>
          </div>
          <p className="text-sm text-muted-foreground">
            From: <span className="font-mono">{inbound_email.from_address}</span>
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Received: {new Date(inbound_email.created_at).toLocaleString()}
          </p>
        </div>

        {/* Email Body */}
        <section className="border rounded-lg">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">Message</h2>
          </div>
          <div className="px-4 py-4">
            {inbound_email.body_html ? (
              <div
                className="prose prose-sm max-w-none text-foreground"
                dangerouslySetInnerHTML={{ __html: inbound_email.body_html }}
              />
            ) : inbound_email.body_plain ? (
              <pre className="text-sm text-foreground whitespace-pre-wrap font-sans">{inbound_email.body_plain}</pre>
            ) : (
              <p className="text-sm text-muted-foreground">(no message body)</p>
            )}
          </div>
        </section>

        {/* Attachments */}
        {inbound_email.attachments.length > 0 && (
          <section className="border rounded-lg">
            <div className="px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground flex items-center gap-2">
                <Paperclip className="w-4 h-4 text-muted-foreground" />
                Attachments ({inbound_email.attachments.length})
              </h2>
            </div>
            <div className="divide-y">
              {inbound_email.attachments.map((att) => (
                <div key={att.id} className="flex items-center justify-between px-4 py-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">{att.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {att.size < 1024 ? `${att.size} B` : att.size < 1048576 ? `${(att.size / 1024).toFixed(1)} KB` : `${(att.size / 1048576).toFixed(1)} MB`}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <DocumentPreviewModal 
                      url={att.url} 
                      filename={att.name || 'Attachment'} 
                    />
                    <a href={att.url} target="_blank" rel="noopener noreferrer">
                      <Button size="sm" variant="ghost" title="Download">
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </DashboardLayout>
  )
}
